import type { DetalheFatura } from '@/types/fatura'
import {
    formatDetalheCategoria,
    formatDetalheValor,
    groupDetalhesAtendimento,
    isDetalheNegativo,
} from '@/services/fatura'
import { formatDate, formatMoney } from '@/utils'

interface FaturaDetalhesItemProps {
    detalhe: DetalheFatura
    isLast?: boolean
    nested?: boolean
}

export default function FaturaDetalhesItem({
    detalhe,
    isLast = false,
    nested = false,
}: FaturaDetalhesItemProps) {
    const dateText = detalhe.Data ? formatDate(detalhe.Data) : ''
    const valorExibido = formatDetalheValor(detalhe, formatMoney)
    const negativo = isDetalheNegativo(detalhe)
    const isPlano = Boolean(detalhe.Plano)

    if (detalhe.Tipo === 3) {
        return (
            <div className={`fatura-detalhe-item${isLast ? ' is-last' : ''}`}>
                <div className="fatura-detalhe-item-header">
                    <span className="fatura-detalhe-badge fatura-detalhe-badge-venda">Venda</span>
                    {detalhe.ValorBase != null && (
                        <span className="fatura-detalhe-valor">{formatMoney(detalhe.ValorBase)}</span>
                    )}
                </div>
                <p className="fatura-detalhe-descricao">{detalhe.Descricao}</p>
            </div>
        )
    }

    if (detalhe.Principal && !nested) {
        const categoria = formatDetalheCategoria(detalhe, dateText)
        const titulo =
            detalhe.Tipo === 2
                ? (detalhe.Frequencia || detalhe.Descricao)
                : detalhe.Descricao

        return (
            <div className={`fatura-detalhe-item is-principal${isLast ? ' is-last' : ''}`}>
                <div className="fatura-detalhe-item-header">
                    {detalhe.Index ? (
                        <span className="fatura-detalhe-codigo">#{detalhe.Index}</span>
                    ) : (
                        <span className="fatura-detalhe-badge fatura-detalhe-badge-atendimento">
                            {detalhe.Tipo === 2 ? 'Plano' : 'Atendimento'}
                        </span>
                    )}
                    {valorExibido && !detalhe.Plano ? (
                        <span className="fatura-detalhe-valor">{valorExibido}</span>
                    ) : null}
                </div>
                {categoria ? <p className="fatura-detalhe-sub">{categoria}</p> : null}
                <p className="fatura-detalhe-descricao is-title">{titulo}</p>
            </div>
        )
    }

    return (
        <div
            className={[
                'fatura-detalhe-item',
                nested ? 'is-nested' : '',
                isLast ? 'is-last' : '',
                negativo ? 'is-negative' : '',
            ].filter(Boolean).join(' ')}
        >
            <div className="fatura-detalhe-item-header">
                <div>
                    {dateText ? <p className="fatura-detalhe-sub">{dateText}</p> : null}
                    <p className="fatura-detalhe-descricao">{detalhe.Descricao}</p>
                </div>
                {valorExibido ? (
                    <span
                        className={[
                            'fatura-detalhe-valor',
                            negativo ? 'is-negative' : '',
                            isPlano ? 'is-plano' : '',
                        ].filter(Boolean).join(' ')}
                    >
                        {valorExibido}
                    </span>
                ) : null}
            </div>
        </div>
    )
}

/** Lista mobile de detalhes agrupados por atendimento/plano. */
export function FaturaDetalhesLista({
    detalhes,
    hidePrincipal = false,
}: {
    detalhes: DetalheFatura[]
    hidePrincipal?: boolean
}) {
    const { grupos, outros } = groupDetalhesAtendimento(detalhes)

    return (
        <div className="fatura-item-detalhes">
            {grupos.map(grupo => {
                const filhos =
                    grupo.principal.Tipo === 2
                        ? grupo.itens.filter(item => !item.Principal)
                        : grupo.itens

                return (
                    <div key={grupo.index} className="fatura-detalhe-grupo">
                        {!hidePrincipal ? (
                            <FaturaDetalhesItem detalhe={grupo.principal} />
                        ) : null}
                        {filhos.length > 0 ? (
                            <div className={`fatura-detalhe-nested${hidePrincipal ? ' is-standalone' : ''}`}>
                                {filhos.map((detalhe, index) => (
                                    <FaturaDetalhesItem
                                        key={`${grupo.index}-${index}`}
                                        detalhe={detalhe}
                                        nested
                                        isLast={index === filhos.length - 1 && outros.length === 0}
                                    />
                                ))}
                            </div>
                        ) : null}
                    </div>
                )
            })}
            {outros.map((detalhe, index) => (
                <FaturaDetalhesItem
                    key={`outro-${index}`}
                    detalhe={detalhe}
                    isLast={index === outros.length - 1}
                />
            ))}
        </div>
    )
}
